import { createFileRoute } from "@tanstack/react-router";
import { jsonLdScript, serviceSchema, breadcrumbSchema, pageHead } from "@/lib/seo";
import { Topbar } from "@/components/calv/Topbar";
import { Footer } from "@/components/calv/Footer";
import { LogoReferences } from "@/components/calv/LogoReferences";
import { Hero } from "@/components/calv/Hero";
import { Section } from "@/components/calv/Section";
import { ProcessFlow } from "@/components/calv/ProcessFlow";
import { Capabilities } from "@/components/calv/Capabilities";

import { CTABlock } from "@/components/calv/CTABlock";
import { RevealOnScroll } from "@/components/calv/RevealOnScroll";
import { RelatedServices } from "@/components/calv/RelatedServices";
import { relatedBySource } from "@/lib/related-services-data";
import { SITE_URL } from "@/lib/locale";

const faqs: { q: string; a: string }[] = [
  {
    q: "What does it cost to have a Shopify store built?",
    a: "The range is wide and depends on assortment, migration depth, the number of apps required, ERP and shipping integrations and the degree of custom work. Flat-rate pricing would be misleading. We give a reliable order of magnitude after the first strategy call once we have framed your actual situation.",
  },
  {
    q: "How long does Shopify store development take?",
    a: "A lean store on a standard theme, without migration and with a manageable assortment, can be delivered in a few weeks. Migrations, ERP and inventory integrations and custom development extend the timeline accordingly. We structure projects in four phases (see Process section) and plan realistically — not in weekend promises.",
  },
  {
    q: "What is the difference between Shopify and Shopify Plus?",
    a: "Shopify Plus is the enterprise tier with extended checkout customization (Checkout Extensibility, Scripts/Functions), higher API limits, automation through Shopify Flow and multi-store setups. Plus pays off when checkout logic, internationalization or volume exceed what standard Shopify can model.",
  },
  {
    q: "Can you migrate my existing shop to Shopify?",
    a: "Yes. We handle migrations from WooCommerce, Shopware and custom builds including product data, customers, orders, URL mapping and 301 redirects. The goal is to preserve both data and existing search rankings.",
  },
  {
    q: "Are you a Shopify Partner?",
    a: "Calvarius is a digital agency with cross-system e-commerce experience since 2013, working operationally with Shopify, Shopware and WooCommerce. We do not hold a formal Shopify Partner certification. Our standard is technical substance and honest system advice, not a badge.",
  },
  {
    q: "Do you offer ongoing care for my Shopify store?",
    a: "Yes. We offer Shopify care on a monthly basis: ongoing technical maintenance, app management, performance optimization and continuous development. For merchants who want a reliable technical partner rather than isolated one-off jobs.",
  },
  {
    q: "Do I need many paid apps for Shopify?",
    a: "No. Every app costs load time and a recurring fee. We select deliberately what is actually needed and prefer to build lean functionality ourselves where it makes technical and economic sense. App sprawl is one of the most common causes of slow and expensive Shopify stores.",
  },
  {
    q: "Do you work with merchants outside Germany?",
    a: "Yes. We are based in Karlstadt am Main (Germany), the collaboration runs location-independently across Germany and the DACH region. Most projects are handled remotely, on-site meetings are possible when needed.",
  },
];

export const Route = createFileRoute("/en/services/ecommerce-development_/shopify-agency")({
  head: () => {
    const h = pageHead("shopifyAgentur", "en");
    return {
      meta: h.meta,
      links: h.links,
      scripts: [
        jsonLdScript(
          serviceSchema({
            name: "Shopify Agency",
            description:
              "Shopify agency for merchants: build a Shopify store, migrate to Shopify, ongoing care, apps and integrations. Cross-system advice, cleanly engineered.",
            url: "/en/services/ecommerce-development/shopify-agency",
            serviceType: "Shopify Agency / Shopify Development",
            areaServed: ["DE"],
          }),
        ),
        jsonLdScript(
          breadcrumbSchema([
            { name: "Home", path: "/en" },
            { name: "Services", path: "/en/services" },
            { name: "E-Commerce Development", path: "/en/services/ecommerce-development" },
            { name: "Shopify Agency", path: "/en/services/ecommerce-development/shopify-agency" },
          ]),
        ),
        jsonLdScript({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
          url: `${SITE_URL}/en/services/ecommerce-development/shopify-agency`,
        }),
      ],
    };
  },
  component: ShopifyAgencyPage,
});

const phases = [
  { num: "01", title: "System decision & data model", body: "We clarify whether Shopify is the right system and structure assortment, pricing logic, variants and product data so they run cleanly in Shopify and in marketing. Typically 3–5 days." },
  { num: "02", title: "Store & checkout", body: "Building or adapting theme and storefront, performant category and product pages, a short and trustworthy checkout. Apps only where actually needed. Typically 2–4 weeks." },
  { num: "03", title: "Apps, integrations & connectivity", body: "Connecting payment, shipping, inventory management and ERP, configuring legally compliant copy and tax rules, plus e-commerce tracking, product feeds and consent management — anchored structurally, not bolted on." },
  { num: "04", title: "Go-live, scaling & care", body: "Testing including checkout and load scenarios, a clean go-live, then support for assortment expansion, performance, new sales channels and ongoing technical care. Available as monthly Shopify care." },
];

const capabilities = [
  { name: "Theme & storefront customization", detail: "Liquid, Sections, Online Store 2.0" },
  { name: "Shopify Plus", detail: "Checkout Extensibility, Scripts/Functions" },
  { name: "App integration & configuration", detail: "deliberate, performance-aware" },
  { name: "Migration to Shopify", detail: "data, URLs, redirects" },
  { name: "Payment & tax integration", detail: "methods, tax rules, invoicing logic" },
  { name: "ERP, inventory & shipping integrations", detail: "automated instead of manual" },
  { name: "E-commerce tracking & feeds", detail: "server-side, consent-compliant" },
  { name: "Performance optimization", detail: "load time even with large catalogues" },
  { name: "Internationalization", detail: "Shopify Markets, languages, currencies" },
];


const mechanics = [
  { title: "Checkout & conversion", body: "Every unnecessary step costs revenue. We keep the checkout short, clear and trustworthy — within what Shopify and Shopify Plus permit in checkout customization." },
  { title: "App selection over app sprawl", body: "Every app costs load time and a monthly fee. We select deliberately and, where sensible, prefer to build lean functionality ourselves." },
  { title: "Product data & feeds", body: "Structured product data as the basis for store, Shopping ads and marketing. Linkable to Delta.", link: { label: "Linked tool: Delta →", href: "/en/services/delta" } },
  { title: "Integrations", body: "Payment, shipping, inventory and ERP mesh automatically, instead of being maintained by hand." },
  { title: "E-commerce tracking", body: "Purchase events, server-side tracking, product feeds and consent management from day one.", link: { label: "More on Tracking & Data Foundation →", href: "/en/services/tracking-data-foundation" } },
  { title: "Legal compliance", body: "Right of withdrawal, price-indication requirements, mandatory disclosures and tax rules implemented correctly." },
];

const services4 = [
  {
    title: "Have a Shopify store built",
    body: "Concept and implementation of a new Shopify or Shopify Plus store — from the data model through theme and checkout to a clean tracking and feed baseline. For merchants starting on Shopify who want it set up correctly from the start.",
  },
  {
    title: "Migration to Shopify",
    body: "Moving from an existing system (e.g. WooCommerce, Shopware or a custom build) to Shopify — including product data, customers, orders, URLs and redirects, so neither data nor search rankings are lost.",
  },
  {
    title: "Shopify care & continuous development",
    body: "Ongoing technical maintenance, app management, performance optimization and further development of existing Shopify stores — including Shopify Plus. For merchants who want a reliable technical partner instead of one-off jobs.",
  },
  {
    title: "Shopify development & integrations",
    body: "Custom work beyond the theme editor: Liquid templating, tailored functionality, app and API integrations, ERP and inventory connectivity. Where standard apps reach their limits.",
  },
];

function ShopifyAgencyPage() {
  return (
    <>
      <Topbar />
      <main id="main">
        <Hero
          variant="short"
          number="01"
          eyebrow="Shopify Agency"
          headline={<>Shopify agency for merchants who treat their store as a <em>selling system.</em></>}
          lead="We build, migrate and maintain Shopify stores — from theme customization through app and integration work to clean e-commerce tracking. For merchants who want a Shopify store built that doesn't just display products, but sells."
          metaStrip={[
            "Shopify & Shopify Plus",
            "Theme & storefront",
            "App & integration work",
            "Migration to Shopify",
            "E-commerce tracking as standard",
          ]}
          primaryCta={{
            eyebrow: "Free · approx. 30 minutes · no obligation",
            label: "Request a strategy call",
            to: "/en/contact",
            search: { topic: "shopify" },
          }}
          secondaryCta={{
            label: "Or: to the broader E-Commerce Development page",
            to: "/en/services/ecommerce-development",
          }}
          breadcrumbs={[
            { label: "Home", to: "/en" },
            { label: "Services", to: "/en/services" },
            { label: "E-Commerce Development", to: "/en/services/ecommerce-development" },
            { label: "Shopify Agency" },
          ]}
        />

        <Section number="02" kicker="Our position" headline={<>Shopify is quick to set up. The hard part is making it <em>actually sell.</em></>}>
          <RevealOnScroll>
            <div className="prose max-w-2xl text-[17px] leading-relaxed text-ink-2">
              <p className="mb-5">Anyone looking for a Shopify agency usually comes from one of three situations: a new store needs to be built, an existing system needs to be migrated to Shopify, or a running Shopify store is underperforming. All three share a costly misconception — that because Shopify is fast to install, it must also be fast to sell. Setting it up is the easy part. The mechanics underneath decide the outcome.</p>
              <p className="mb-5">Shopify takes hosting, updates and security off your plate — that's the strength of the system. What it doesn't take off your plate: a checkout that doesn't drop off unnecessarily, product data that lives in a system instead of spreadsheets, apps that don't ruin load time, integrations to inventory management, ERP and shipping that fire automatically, and an e-commerce tracking setup that captures purchase events and product feeds cleanly from the start. That is where we work as Shopify developers — not on the theme editor, but on what decides revenue underneath.</p>
              <p>Shopify isn't the right system for every merchant. For brand-led merchants with a manageable to mid-sized assortment, high standards on design and a quick go-live, it is often the best choice. Anyone needing deeply customized B2B logic, highly individualized processes or very large assortments with special rules is often better served by Shopware. We say so openly — even when that means recommending against Shopify. Which system fits your business model is something we clarify before the first line of code.</p>
            </div>
            <div className="mt-10 rounded-lg border-l-4 border-lime bg-bg-subtle p-5 text-[15px] leading-relaxed text-ink-2">
              Unsure between Shopify and Shopware?{" "}
              <a href="#systemvergleich" className="font-medium text-ink underline decoration-lime decoration-2 underline-offset-4 hover:decoration-ink">
                Jump to the system comparison →
              </a>{" "}
              · Or take the cross-system view:{" "}
              <a href="/en/services/ecommerce-development" className="font-medium text-ink underline decoration-lime decoration-2 underline-offset-4 hover:decoration-ink">
                E-Commerce Development →
              </a>
            </div>
          </RevealOnScroll>
        </Section>

        <Section
          variant="tinted"
          number="03"
          kicker="Our approach"
          headline={<>From the <em>system decision</em> to a running Shopify store.</>}
          caption="How we structure Shopify projects — realistically scheduled, not in weekend promises."
        >
          <ProcessFlow steps={phases} />
        </Section>

        <Section
          number="04"
          kicker="Services"
          headline={<>What we cover as a <em>Shopify agency.</em></>}
          caption="Four focus areas — individually or combined, depending on the situation."
        >
          <RevealOnScroll>
            <div className="grid gap-6 md:grid-cols-2">
              {services4.map((s) => (
                <div key={s.title} className="rounded-lg border border-rule bg-white p-7">
                  <h3 className="mb-3 font-display text-xl text-ink">{s.title}</h3>
                  <p className="text-[15px] leading-relaxed text-ink-2">{s.body}</p>
                </div>
              ))}
            </div>
            <div className="mt-10">
              <CTABlock
                headline={<>Which situation describes <em>you?</em></>}
                body="Whether a new Shopify store, a migration to Shopify or an existing store that should do more — we clarify it in a strategy call."
                primary={{ label: "Request a strategy call", to: "/en/contact", search: { topic: "shopify" } }}
              />
            </div>
          </RevealOnScroll>
        </Section>

        <Section
          number="05"
          kicker="Mechanics"
          headline={<>What turns a Shopify store into a <em>selling system.</em></>}
          caption="The topics that decide revenue — and that the theme editor alone doesn't solve."
        >
          <RevealOnScroll>
            <div className="grid gap-6 md:grid-cols-2">
              {mechanics.map((m) => (
                <div key={m.title} className="rounded-lg border border-rule bg-white p-7">
                  <h3 className="mb-3 font-display text-xl text-ink">{m.title}</h3>
                  <p className="text-[15px] leading-relaxed text-ink-2">{m.body}</p>
                  {m.link && (
                    <a href={m.link.href} className="mt-4 inline-flex items-center font-mono text-xs uppercase tracking-wider text-ink-2 hover:text-lime-deep">
                      {m.link.label}
                    </a>
                  )}
                </div>
              ))}
            </div>
          </RevealOnScroll>
        </Section>

        <Section number="06" kicker="Capabilities" headline={<>How we make Shopify stores <em>built to sell.</em></>}>
          <Capabilities items={capabilities} />
        </Section>

        <Section
          id="systemvergleich"
          variant="tinted"
          number="07"
          kicker="System comparison"
          headline={<>Shopify or Shopware — which system fits <em>which merchant.</em></>}
          caption="An honest framing instead of platform marketing."
        >
          <RevealOnScroll>
            <div className="grid gap-6 md:grid-cols-2">
              <div className="rounded-lg border border-rule bg-white p-7">
                <h3 className="mb-4 font-display text-xl text-ink">Shopify is usually right for:</h3>
                <ul className="space-y-3 text-[15px] leading-relaxed text-ink-2">
                  <li className="flex gap-3"><span className="text-cyan" aria-hidden>●</span><span>brand-led merchants with their own assortment and high standards on design and look &amp; feel</span></li>
                  <li className="flex gap-3"><span className="text-cyan" aria-hidden>●</span><span>merchants who want to go live quickly without their own server and maintenance burden</span></li>
                  <li className="flex gap-3"><span className="text-cyan" aria-hidden>●</span><span>small to mid-sized assortments with manageable variant logic</span></li>
                  <li className="flex gap-3"><span className="text-cyan" aria-hidden>●</span><span>clear, largely standardizable sales processes</span></li>
                </ul>
              </div>
              <div className="rounded-lg border border-rule bg-white p-7">
                <h3 className="mb-4 font-display text-xl text-ink">Shopware is usually right for:</h3>
                <ul className="space-y-3 text-[15px] leading-relaxed text-ink-2">
                  <li className="flex gap-3"><span className="text-cyan" aria-hidden>●</span><span>very large assortments or complex variant structures</span></li>
                  <li className="flex gap-3"><span className="text-cyan" aria-hidden>●</span><span>deeply customized or rule-based processes (B2B, tiered pricing, approvals)</span></li>
                  <li className="flex gap-3"><span className="text-cyan" aria-hidden>●</span><span>strongly international/multilingual setups with special requirements</span></li>
                  <li className="flex gap-3"><span className="text-cyan" aria-hidden>●</span><span>merchants who need full source-code control</span></li>
                </ul>
              </div>
            </div>
            <p className="mt-8 max-w-2xl text-[15px] leading-relaxed text-ink-2">
              Both systems are mature. The decision isn't made on a feature list but on your business model, your assortment and your processes. We clarify that upfront — and will also say so when neither one is the best fit.{" "}
              <a href="/en/services/ecommerce-development" className="font-medium text-ink underline decoration-lime decoration-2 underline-offset-4 hover:decoration-ink">
                To the cross-system E-Commerce Development view →
              </a>
            </p>
          </RevealOnScroll>
        </Section>

        <LogoReferences locale="en" number="08" />

        <Section number="09" kicker="FAQ" headline={<>Common questions about the <em>Shopify agency.</em></>}>
          <RevealOnScroll>
            <div className="divide-y divide-rule rounded-lg border border-rule bg-white">
              {faqs.map((f) => (
                <details key={f.q} className="group p-6">
                  <summary className="cursor-pointer list-none font-display text-lg text-ink marker:hidden">
                    <span className="mr-3 text-cyan-deep">+</span>
                    {f.q}
                  </summary>
                  <p className="mt-4 pl-6 text-[15px] leading-relaxed text-ink-2">{f.a}</p>
                </details>
              ))}
            </div>
          </RevealOnScroll>
        </Section>

        <RelatedServices
          locale="en"
          fromId="shopify-agentur"
          number="10"
          items={relatedBySource["shopify-agentur"]}
        />

        <Section number="11" kicker="Engagement">
          <CTABlock
            headline={<>Let's talk about your <em>Shopify store.</em></>}
            body="Whether a new Shopify store, a migration to Shopify or an existing store that should do more — in a strategy call we clarify what fits your assortment, your logistics and your business model. Concrete, honest and without sales pressure. If Shopify isn't the right system, we'll say so too."
            primary={{ label: "Request a strategy call", to: "/en/contact", search: { topic: "shopify" } }}
            secondary={{ label: "All services", to: "/en/services" }}
          />
        </Section>
      </main>
      <Footer />
    </>
  );
}
